import { StageDragResult, StageGroup, GroupedStage, StageInfo, HIDDEN_STAGE_TYPE } from '../types/stageTypes';
import { DefaultStageMappingType } from '../../../api/integrations';

/**
 * Finds a stage in the groups based on its droppable ID and index
 * @param stageGroups All stage groups
 * @param droppableId The ID of the droppable area
 * @param index The index within the droppable area
 * @returns The found stage or undefined
 */
export const findStageInGroups = (
    stageGroups: StageGroup[],
    droppableId: string,
    index: number
): GroupedStage | undefined => {
    for (const group of stageGroups) {
        if (group.stageType === droppableId) {
            return group.stages[index];
        }
    }
    return undefined;
};

/**
 * Determines the target mapping type and unmapped state based on destination group
 * @param destinationId The ID of the destination droppable area
 * @returns Object containing the new stage type and unmapped state
 */
export const determineTargetMapping = (destinationId: string): {
    stageType: DefaultStageMappingType | null;
    isUnmapped: boolean;
} => {
    if (destinationId === HIDDEN_STAGE_TYPE) {
        return { stageType: null, isUnmapped: true };
    } else if (destinationId === 'UNMAPPED') {
        return { stageType: null, isUnmapped: false };
    } else {
        // Dropped into a standard stage type group
        return {
            stageType: destinationId as DefaultStageMappingType,
            isUnmapped: false
        };
    }
};

/**
 * Updates the order of interview stages after a drag operation
 * @param stages All stages to update
 * @param draggedStageId ID of the stage being dragged
 * @param destinationIndex New position index
 * @returns Updated stages with corrected order values
 */
export const updateInterviewStageOrder = (
    stages: StageInfo[],
    draggedStageId: string,
    destinationIndex: number
): StageInfo[] => {
    // Get all interview stages (excluding the dragged one which we'll reinsert)
    let interviewStages = stages.filter(s =>
        s.mapping.stage_type === 'INTERVIEW' &&
        !s.mapping.is_unmapped &&
        s.id !== draggedStageId
    );

    // Sort the current interview stages by their existing order
    interviewStages.sort((a, b) => (a.mapping.order || 0) - (b.mapping.order || 0));

    // Add the dragged stage at the correct position
    const draggedStage = stages.find(s => s.id === draggedStageId);
    if (draggedStage) {
        interviewStages.splice(destinationIndex, 0, draggedStage);
    }

    // Create a map of current orders for comparison
    const originalOrders = new Map<string, number>();
    interviewStages.forEach(s => {
        originalOrders.set(s.id, s.mapping.order || 0);
    });

    // Update order for all interview stages and mark as changed
    interviewStages.forEach((s, index) => {
        const originalOrder = originalOrders.get(s.id) || 0;
        // Only mark as changed if the order actually changed
        if (originalOrder !== index) {
            s.isChanged = true;
        }
        s.mapping.order = index;
    });

    // Return all stages with updated orders
    return stages.map(s => {
        const updatedInterviewStage = interviewStages.find(is => is.id === s.id);
        if (updatedInterviewStage) {
            return {
                ...s,
                mapping: {
                    ...s.mapping,
                    order: updatedInterviewStage.mapping.order
                },
                isChanged: updatedInterviewStage.isChanged || s.isChanged || false
            };
        }
        return s;
    });
};

/**
 * Reorders the remaining interview stages after one is removed
 * @param stages All stages to update
 * @param excludedStageId ID of the stage to exclude from interview ordering
 * @returns Updated stages with corrected order values
 */
export const reorderRemainingInterviewStages = (
    stages: StageInfo[],
    excludedStageId: string
): StageInfo[] => {
    // Get all interview stages except the excluded one
    const interviewStages = stages.filter(s =>
        s.mapping.stage_type === 'INTERVIEW' &&
        !s.mapping.is_unmapped &&
        s.id !== excludedStageId
    );

    // Sort by existing order
    interviewStages.sort((a, b) => (a.mapping.order || 0) - (b.mapping.order || 0));

    // Reassign sequential order values and mark as changed
    interviewStages.forEach((stage, idx) => {
        const originalOrder = stage.mapping.order || 0;
        // Only mark as changed if the order actually changed
        if (originalOrder !== idx) {
            stage.isChanged = true;
        }
        stage.mapping.order = idx;
    });

    // Return all stages with updated orders
    return stages.map(s => {
        if (s.id === excludedStageId) {
            // For the excluded stage, reset its order
            return {
                ...s,
                mapping: {
                    ...s.mapping,
                    order: 0
                }
            };
        }

        const updatedInterviewStage = interviewStages.find(is => is.id === s.id);
        if (updatedInterviewStage) {
            return {
                ...s,
                mapping: {
                    ...s.mapping,
                    order: updatedInterviewStage.mapping.order
                },
                isChanged: updatedInterviewStage.isChanged || s.isChanged || false
            };
        }
        return s;
    });
};

/**
 * Processes the drag end event from react-beautiful-dnd
 * @param result The drag result from react-beautiful-dnd
 * @param stageGroups Current stage groups
 * @param originalStages Original stage data
 * @returns Updated stage info based on the drag operation
 */
export const processDragEnd = (
    result: StageDragResult,
    stageGroups: StageGroup[],
    originalStages: StageInfo[]
): StageInfo[] | null => {
    const { source, destination } = result;

    // If dropped outside a droppable area
    if (!destination) return null;

    const sourceDroppableId = source.droppableId;
    const destinationDroppableId = destination.droppableId;

    // Find the dragged stage
    const draggedStage = findStageInGroups(stageGroups, sourceDroppableId, source.index);
    if (!draggedStage) {
        console.error('Dragged stage not found');
        return null;
    }

    // Determine the target mapping based on destination
    const { stageType: newStageType, isUnmapped: newIsUnmapped } =
        determineTargetMapping(destinationDroppableId);

    // Create a map of all stages to preserve any existing changes
    const allStagesMap = new Map<string, StageInfo>();

    originalStages.forEach(os => {
        // Look for the current state of this stage in the groups
        let currentGroupedStage: GroupedStage | undefined;
        for (const group of stageGroups) {
            const stage = group.stages.find(s => s.originalId === os.id);
            if (stage) {
                currentGroupedStage = stage;
                break;
            }
        }

        // Base mapping from original with any current changes applied
        const baseMapping = { ...os.mapping };
        const currentMappingChanges = currentGroupedStage ? { ...currentGroupedStage.mapping } : {};

        allStagesMap.set(os.id, {
            id: os.id,
            name: os.name,
            mapping: {
                ...baseMapping,
                ...currentMappingChanges,
                order: currentGroupedStage?.mapping?.order ?? baseMapping.order ?? 0,
            },
            isNew: os.isNew,
            isChanged: (currentGroupedStage?.isChanged || false) || (os.id === draggedStage.originalId),
        });
    });

    // Update the dragged stage's mapping
    const draggedStageInfo = allStagesMap.get(draggedStage.originalId);
    if (draggedStageInfo) {
        draggedStageInfo.mapping.stage_type = newStageType;
        draggedStageInfo.mapping.is_unmapped = newIsUnmapped;
        draggedStageInfo.isChanged = true;
    } else {
        return null;
    }

    // Get all stages with potentially updated mappings
    let allStagesUpdated = Array.from(allStagesMap.values());

    // If moving to interview group, update ordering
    if (destinationDroppableId === 'INTERVIEW') {
        allStagesUpdated = updateInterviewStageOrder(
            allStagesUpdated,
            draggedStage.originalId,
            destination.index
        );
    }
    // If moving FROM interview group TO another group, reorder remaining interview stages
    else if (sourceDroppableId === 'INTERVIEW' && destinationDroppableId !== 'INTERVIEW') {
        // Update the dragged stage's order (reset it)
        if (draggedStageInfo) {
            draggedStageInfo.mapping.order = 0;
        }

        // Reorder the remaining interview stages to close the gap
        allStagesUpdated = reorderRemainingInterviewStages(
            allStagesUpdated,
            draggedStage.originalId
        );
    }

    return allStagesUpdated;
}; 